import React from 'react';
import { WarningFilled } from '@ant-design/icons';
import { formatCount } from '../../helpers/inventory-groups';

export type DeliveryTarget = {
  /** Target impressions, as used by the API (pro-rated when the start date is past). */
  target: number;
  /** Available impressions before the target is taken out. */
  available: number;
};

type Props = {
  targetImpressions: number;
  availableImpressions: number;
};

// Same scale and zones as the legacy Ad Planner gauge: the needle shows the share of
// available inventory left after the target, 100 * (1 - target / available).
const ZONES = [
  { from: 0, to: 15, color: '#cb3839' },
  { from: 15, to: 45, color: '#fbcf9c' },
  { from: 45, to: 100, color: '#3d9b4d' }
];

const CX = 100;
const CY = 100;
const RADIUS = 80;
const STROKE = 12;

/** Point on the arc for a gauge value from 0 (left) to 100 (right). */
const pointAt = (value: number, radius: number) => {
  const angle = Math.PI - (value / 100) * Math.PI;
  return { x: CX + radius * Math.cos(angle), y: CY - radius * Math.sin(angle) };
};

const arcPath = (from: number, to: number) => {
  const start = pointAt(from, RADIUS);
  const end = pointAt(to, RADIUS);
  return `M ${start.x} ${start.y} A ${RADIUS} ${RADIUS} 0 0 1 ${end.x} ${end.y}`;
};

export const deliveryHeadroom = (target: number, available: number): number => {
  if (available <= 0) return -100;
  return Math.round(100 * (1 - target / available) * 10) / 10;
};

export default function DeliveryMeter({ targetImpressions, availableImpressions }: Props) {
  const headroom = deliveryHeadroom(targetImpressions, availableImpressions);
  const needleValue = Math.min(Math.max(headroom, 0), 100);
  const tip = pointAt(needleValue, RADIUS * 0.85);
  // Needle drawn as a thin triangle: a wide base around the hub, a point at the tip.
  const angle = Math.PI - (needleValue / 100) * Math.PI;
  const baseHalf = 4;
  const baseA = {
    x: CX + baseHalf * Math.cos(angle + Math.PI / 2),
    y: CY - baseHalf * Math.sin(angle + Math.PI / 2)
  };
  const baseB = {
    x: CX + baseHalf * Math.cos(angle - Math.PI / 2),
    y: CY - baseHalf * Math.sin(angle - Math.PI / 2)
  };

  const shortfall = Math.max(targetImpressions - availableImpressions, 0);
  let status: { tone: 'risk' | 'tight' | 'good'; text: string };
  if (shortfall > 0) {
    status = {
      tone: 'risk',
      text: `Target not likely to deliver: ${formatCount(shortfall)} impressions short`
    };
  } else if (headroom <= 15) {
    status = { tone: 'risk', text: 'Target not likely to deliver' };
  } else if (headroom <= 45) {
    status = { tone: 'tight', text: 'Target can deliver, with little headroom' };
  } else {
    status = { tone: 'good', text: 'Target likely to deliver' };
  }

  return (
    <div className="delivery-meter">
      <svg
        className="delivery-meter-gauge"
        viewBox="0 0 200 112"
        role="meter"
        aria-label="Inventory left after the target"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={needleValue}
        aria-valuetext={`${headroom}% of available inventory left after the target. ${status.text}.`}
      >
        {ZONES.map((zone) => (
          <path
            key={zone.from}
            d={arcPath(zone.from, zone.to)}
            fill="none"
            stroke={zone.color}
            strokeWidth={STROKE}
          />
        ))}
        <polygon
          points={`${baseA.x},${baseA.y} ${tip.x},${tip.y} ${baseB.x},${baseB.y}`}
          fill="#000"
        />
        <circle cx={CX} cy={CY} r={5} fill="#000" />
      </svg>
      <div className="delivery-meter-text">
        <div className="delivery-meter-title">Delivery</div>
        <div className={`delivery-meter-status ${status.tone}`}>
          {status.tone !== 'good' && <WarningFilled />} {status.text}
        </div>
        {shortfall === 0 && (
          <div className="inventory-metric-hint">{headroom}% left after the target</div>
        )}
      </div>
    </div>
  );
}
